import { useEffect } from 'react';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Navigate,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useParams
} from 'react-router';
import type { Route } from './+types/root';
import i18n, { DEFAULT_LOCALE, isLocale } from './i18n';
import { favouritesStore } from './stores';
import './styles/index.css';

export function Layout({ children }: { children: React.ReactNode }) {
  // Layout renders outside the router context — router hooks like useParams
  // are unavailable here. App handles lang sync and redirects instead.
  return (
    <html>
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
        <title>tata travel</title>
        <link rel="icon" href="/favicon.ico" sizes="any" />
        <link
          rel="icon"
          type="image/png"
          sizes="64x64"
          href="/favicon-64.png"
        />
        {/* iOS home screen: only reads apple-touch-icon, and it must be opaque */}
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        {/* Android home screen reads its icons from the manifest */}
        <link rel="manifest" href="/site.webmanifest" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  const { lang } = useParams();
  const location = useLocation();

  useEffect(() => {
    if (lang) document.documentElement.lang = lang;
  }, [lang]);

  // Bookmark buttons need to know current favourite state; load it once up
  // front so it's ready by the time any POI card/summary renders.
  useEffect(() => {
    favouritesStore.load();
  }, []);

  // Unknown locale (e.g. /fr): swap the segment for the default and redirect,
  // so the rest of the app only ever sees a supported lang.
  if (!lang || !isLocale(lang)) {
    const rest = location.pathname.replace(/^\/[^/]+/, '');
    return (
      <Navigate to={`/${DEFAULT_LOCALE}${rest}${location.search}`} replace />
    );
  }

  i18n.changeLanguage(lang);
  return <Outlet />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = 'Oops!';
  let details = 'An unexpected error occurred.';
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : 'Error';
    details =
      error.status === 404
        ? 'The requested page could not be found.'
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main>
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre>
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
